import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { GlobalMessageType, RoutingService } from '@spartacus/core'
import { FileSaverService } from 'ngx-filesaver'
import { finalize } from 'rxjs/operators'
import { AgreementStatus } from '../../../../../models/enum/agreement.enum'
import { CustomRoutes } from '../../../../../models/enum/custom-routes.enum'
import { SharedValue } from '../../../../../models/enum/shared.enum'
import { IAgreement } from '../../../../../models/interface/agreement.interface'
import { IHistoryList } from '../../../../../models/interface/history-list.interface'
import { ISharedPagination } from '../../../../../models/interface/shared.interface'
import { PriceSupportAgreementService } from '../../../../../services/agreement/price-support/price-support-agreement.service'
import { CsvService } from '../../../../../services/shared/csv/csv.service'
import { CustomGlobalMessageService } from '../../../../../services/shared/custom-global-message/custom-global-message.service'
import { CustomModalService } from '../../../../../services/shared/modal/custom-modal.service'
import { SharedService } from '../../../../../services/shared/shared.service'
import { ICustomSelectOption } from '../../../../shared/custom-components/custom-select/modals/interface/custom-select.interface'
import { CustomTableColumnType } from '../../../../shared/custom-components/custom-table/modals/enum/custom-table.enum'
import {
  ICustomTableColumn,
  ICustomTableHeader
} from '../../../../shared/custom-components/custom-table/modals/interface/custom-table.interface'
import { PriceAgreementAcceptComponent } from '../price-agreement-accept-reject/price-agreement-accept/price-agreement-accept.component'
import { PriceAgreementRejectComponent } from '../price-agreement-accept-reject/price-agreement-reject/price-agreement-reject.component'
import { UpdateAccountNumberComponent } from '../update-account-number/update-account-number.component'

@Component({
    selector: 'app-price-agreement-history',
    templateUrl: './price-agreement-history.component.html',
    styleUrls: ['./price-agreement-history.component.scss'],
    standalone: false
})
export class PriceAgreementHistoryComponent implements OnInit {
  public agreementList: IHistoryList
  public showInitialLoader = true
  public showLoaderOnPagination: boolean
  public selectedSort: string
  public selectedFilter: string
  public selectedPage = 1
  public downloadingCsv = false
  public selectedSearch: string

  public filterOptions: ICustomSelectOption[] = [
    { label: 'priceSupportAgreement.placed', value: `${AgreementStatus.OFFER},${AgreementStatus.SUBMITTED}` },
    { label: 'priceSupportAgreement.accepted', value: AgreementStatus.ACCEPTED },
    { label: 'priceSupportAgreement.rejected', value: AgreementStatus.REJECTED },
    { label: 'priceSupportAgreement.pending', value: AgreementStatus.PENDING },
    { label: 'priceSupportAgreement.expired', value: AgreementStatus.EXPIRED }
  ]

  public sortOptions: ICustomSelectOption[] = [
    { label: 'priceSupportAgreement.startDate', value: 'startDate' },
    { label: 'priceSupportAgreement.endDate', value: 'quoteExpirationDate' }
  ]

  public headers: ICustomTableHeader[] = [
    { title: 'priceSupportAgreement.agreementNumber' },
    { title: 'priceSupportAgreement.endUserName' },
    { title: 'priceSupportAgreement.endUserNumber' },
    { title: 'priceSupportAgreement.distributorEndUserAccount' },
    { title: 'priceSupportAgreement.startDate' },
    { title: 'priceSupportAgreement.status' },
    { title: 'shared.actions', type: CustomTableColumnType.BUTTONS }
  ]

  public columns: ICustomTableColumn[] = [
    { key: 'orderCode', type: CustomTableColumnType.LINK, link: { handler: (res) => this.viewDetails(res) } },
    { key: 'endCustomer.orgUnit.name' },
    { key: 'endCustomer.orgUnit.uid' },
    {
      key: 'accountNumber',
      type: CustomTableColumnType.UPDATE_BUTTON,
      button: {
        title: '',
        handler: (res) => this.updateAccountNumber(res)
      }
    },
    { key: 'startDate', type: CustomTableColumnType.DATE },
    { key: 'stateDisplay' },
    {
      key: 'actions',
      type: CustomTableColumnType.BUTTONS,
      buttons: [
        {
          title: 'shared.details',
          translate: true,
          icon: 'fa fa-info-circle',
          handler: (res) => this.viewDetails(res)
        },
        {
          title: 'priceSupportAgreement.accept',
          translate: true,
          icon: 'fa fa-check',
          handler: (res) => this.acceptOrRejectAgreement(true, res)
        },
        {
          title: 'priceSupportAgreement.reject',
          translate: true,
          icon: 'fa fa-times',
          handler: (res) => this.acceptOrRejectAgreement(false, res)
        }
      ]
    }
  ]

  constructor(
    public priceSupportAgreementService: PriceSupportAgreementService,
    public globalMessageService: CustomGlobalMessageService,
    private changeDetectorRef: ChangeDetectorRef,
    private csvService: CsvService,
    public routingService: RoutingService,
    private modalService: CustomModalService,
    private fileSaverService: FileSaverService,
    private sharedService: SharedService
  ) {}

  ngOnInit(): void {
    this.sharedService
      .getQueryParams()
      .subscribe((queryParams) => {
        if (queryParams?.page) {
          this.pageChange(queryParams.page)
        } else {
          this.pageChange(1)
        }
      })
      .unsubscribe()
  }

  public downloadCSV(): void {
    this.downloadingCsv = true
    const fileType = '.csv'
    const fileName = 'Price Support Agreement History' + fileType
    this.priceSupportAgreementService.downloadCSV(this.agreementList.pagination.totalCount).subscribe(
      (csv) => {
        this.downloadingCsv = false
        this.fileSaverService.save(csv, fileName)
        this.changeDetectorRef.detectChanges()
      },
      (err) => {
        this.downloadingCsv = false
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
        this.changeDetectorRef.detectChanges()
      }
    )
  }

  public get disableWhenNoDataOrDownloading(): boolean {
    return !this.agreementList?.results?.length || this.downloadingCsv
  }

  public viewDetails(agreement: IAgreement): void {
    const queryParam = {
      [CustomRoutes.PRICE_SUPPORT_AGREEMENT_HISTORY]: { page: this.selectedPage }
    }
    this.sharedService.setQueryParamsLocal(queryParam)

    // TODO:Spartacus - 'RoutingService.go' changed signature. Before 4.0, the object with query params could be passed in the 2nd argument. Now the 2nd argument is Angular NavigationExtras object (with 'queryParams' property).
    this.routingService.go({
      cxRoute: CustomRoutes.PRICE_SUPPORT_AGREEMENT_DETAILS,
      params: { code: agreement.orderCode }
    })
  }

  public pageChange(value: number): void {
    // TODO:Spartacus - 'RoutingService.go' changed signature. Before 4.0, the object with query params could be passed in the 2nd argument. Now the 2nd argument is Angular NavigationExtras object (with 'queryParams' property).
    this.routingService.go(
      {
        cxRoute: CustomRoutes.PRICE_SUPPORT_AGREEMENT_HISTORY
        // Sagar
        // }, null, {queryParams: {page: value}});
      },
      { queryParams: { page: value } }
    )

    this.selectedPage = value
    this.changePageData({
      pageNumber: value,
      sort: this.selectedSort,
      status: this.selectedFilter,
      searchText: this.selectedSearch
    })
  }

  public filterChange(value: string): void {
    this.selectedFilter = value
    this.changePageData({
      status: value,
      sort: this.selectedSort,
      searchText: this.selectedSearch
    })
  }

  public sortChange(value: string): void {
    this.selectedSort = value
    this.changePageData({
      status: this.selectedFilter,
      sort: value,
      searchText: this.selectedSearch
    })
  }

  public changePageData(params: ISharedPagination): void {
    this.showLoaderOnPagination = true
    this.priceSupportAgreementService
      .getAgreements(params)
      .pipe(
        finalize(() => {
          this.showInitialLoader = false
          this.showLoaderOnPagination = false
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe((res) => {
        if (!res) {
          this.globalMessageService.add(SharedValue.errorTryAgain, GlobalMessageType.MSG_TYPE_ERROR)
        } else {
          this.agreementList = res
        }
      })
  }

  public refreshPageData(): void {
    this.changePageData({
      pageNumber: this.selectedPage,
      sort: this.selectedSort,
      status: this.selectedFilter,
      searchText: this.selectedSearch
    })
  }

  public get showMessageWhenNoData(): boolean {
    return !this.showLoaderOnPagination
  }

  public get showLoaderOnStartupOrWhenNoData(): boolean {
    return !this.agreementList?.results?.length && this.showLoaderOnPagination
  }

  public acceptOrRejectAgreement(accept: boolean, agreement: IAgreement): void {
    const agreementComponent = accept ? PriceAgreementAcceptComponent : PriceAgreementRejectComponent

    const data = {
      showDetails: true,
      agreement
    }

    this.modalService
      .showModal(agreementComponent, data)
      .then((code) => {
        if (!code) {
          return
        }

        this.refreshPageData()
        this.changeDetectorRef.detectChanges()
      })
      .catch((err) => {
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
      })
  }

  public updateAccountNumber(value: any): void {
    this.modalService
      .showModal(UpdateAccountNumberComponent, value)
      .then((reload) => {
        if (!reload) {
          return
        }

        this.refreshPageData()
        this.changeDetectorRef.detectChanges()
        this.globalMessageService.add(
          { key: 'priceSupportAgreement.accountNumberUpdated' },
          GlobalMessageType.MSG_TYPE_CONFIRMATION
        )
      })
      .catch((err) => {
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
      })
  }

  public onSearch(value): void {
    this.selectedSearch = value?.trim()
    this.changePageData({
      sort: this.selectedSort,
      status: this.selectedFilter,
      searchText: this.selectedSearch
    })
  }
}
